/**
 * Job Types Configuration Service
 *
 * UNIFIED ARCHITECTURE:
 * - Single JOB_TYPE_CONFIGS map for ALL job types
 * - category: "standard" | "advanced" determines UI grouping
 * - status: "active" | "coming_soon" | "beta" determines availability
 * - isConfigBased: true for jobs that need config form instead of file upload
 * - externalLink: URL to open in new tab (e.g., LIDAR → mesh-detecto)
 * - iconName: String name of Lucide icon (converted to JSX in components)
 */

import { JobType } from "../types";

// ============================================================================
// JOB TYPE CONFIG INTERFACE
// ============================================================================

export interface JobTypeConfig {
  type: JobType;
  label: string;
  iconName: string; // Lucide icon name (e.g., "Users", "Car")
  description: string;
  category: "standard" | "advanced";
  status: "active" | "coming_soon" | "beta";
  isConfigBased: boolean;
  externalService?: string;
  externalLink?: string;
  acceptedFileTypes: string[];
  acceptedExtensions: string[];
  maxFileSize: number;
  color: string;
}

// ============================================================================
// UNIFIED JOB TYPE CONFIGURATIONS
// ============================================================================

export const JOB_TYPE_CONFIGS: Record<JobType, JobTypeConfig> = {
  // ─────────────────────────────────────────────────────────────────────────
  // STANDARD ANALYSIS TYPES
  // ─────────────────────────────────────────────────────────────────────────

  [JobType.PEOPLE_COUNTING]: {
    type: JobType.PEOPLE_COUNTING,
    label: "People Counting",
    iconName: "Users",
    description: "Analyze footfall and monitor exits for safety and compliance",
    category: "standard",
    status: "active",
    isConfigBased: false,
    color: "bg-blue-500",
    acceptedFileTypes: [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/quicktime",
      "video/x-msvideo",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ],
    acceptedExtensions: [".mp4", ".avi", ".mov", ".jpg", ".jpeg", ".png"],
    maxFileSize: 500,
  },

  [JobType.NUMBER_PLATE_DETECTION]: {
    type: JobType.NUMBER_PLATE_DETECTION,
    label: "Number Plate Detection",
    iconName: "Car",
    description: "Detect and extract number plates from vehicles",
    category: "standard",
    status: "active",
    isConfigBased: false,
    color: "bg-purple-500",
    acceptedFileTypes: [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/quicktime",
      "video/x-msvideo",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ],
    acceptedExtensions: [".mp4", ".avi", ".mov", ".jpg", ".jpeg", ".png"],
    maxFileSize: 500,
  },

  [JobType.PARKING_ANALYSIS]: {
    type: JobType.PARKING_ANALYSIS,
    label: "Parking Analysis",
    iconName: "ParkingCircle",
    description: "Analyze the movement of vehicles in parking zones",
    category: "standard",
    status: "active",
    isConfigBased: false,
    color: "bg-indigo-500",
    acceptedFileTypes: [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/quicktime",
      "video/x-msvideo",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ],
    acceptedExtensions: [".mp4", ".avi", ".mov", ".jpg", ".jpeg", ".png"],
    maxFileSize: 500,
  },

  [JobType.IN_OUT_COUNTING]: {
    type: JobType.IN_OUT_COUNTING,
    label: "In/Out Counting",
    iconName: "ArrowLeftRight",
    description: "Track people entering and exiting areas",
    category: "standard",
    status: "active",
    isConfigBased: false,
    color: "bg-teal-500",
    acceptedFileTypes: [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/quicktime",
      "video/x-msvideo",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ],
    acceptedExtensions: [".mp4", ".avi", ".mov", ".jpg", ".jpeg", ".png"],
    maxFileSize: 500,
  },

  [JobType.POTHOLE_DETECTION]: {
    type: JobType.POTHOLE_DETECTION,
    label: "Pothole Detection",
    iconName: "Construction",
    description: "Detect and locate potholes in road videos",
    category: "standard",
    status: "active",
    isConfigBased: false,
    color: "bg-orange-500",
    acceptedFileTypes: [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/quicktime",
      "video/x-msvideo",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ],
    acceptedExtensions: [".mp4", ".avi", ".mov", ".jpg", ".jpeg", ".png"],
    maxFileSize: 500,
  },

  [JobType.FOOD_WASTE_ESTIMATION]: {
    type: JobType.FOOD_WASTE_ESTIMATION,
    label: "Food Waste Estimation",
    iconName: "UtensilsCrossed",
    description: "Estimate food waste from video analysis",
    category: "standard",
    status: "active",
    isConfigBased: false,
    color: "bg-yellow-500",
    acceptedFileTypes: [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/quicktime",
      "video/x-msvideo",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ],
    acceptedExtensions: [".mp4", ".avi", ".mov", ".jpg", ".jpeg", ".png"],
    maxFileSize: 500,
  },

  [JobType.ROOM_READINESS]: {
    type: JobType.ROOM_READINESS,
    label: "Room Readiness",
    iconName: "Bed",
    description: "Automated inspection for clean and guest-ready rooms",
    category: "standard",
    status: "active",
    isConfigBased: false,
    color: "bg-cyan-500",
    acceptedFileTypes: [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/quicktime",
      "video/x-msvideo",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ],
    acceptedExtensions: [".mp4", ".avi", ".mov", ".jpg", ".jpeg", ".png"],
    maxFileSize: 500,
  },

  [JobType.LOBBY_CROWD_DETECTION]: {
    type: JobType.LOBBY_CROWD_DETECTION,
    label: "Lobby Crowd Detection",
    iconName: "Users2",
    description: "Monitor lobby occupancy and detect crowd alerts",
    category: "standard",
    status: "active",
    isConfigBased: false,
    color: "bg-pink-500",
    acceptedFileTypes: [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/quicktime",
      "video/x-msvideo",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ],
    acceptedExtensions: [".mp4", ".avi", ".mov", ".jpg", ".jpeg", ".png"],
    maxFileSize: 500,
  },

  [JobType.WILDLIFE_DETECTION]: {
    type: JobType.WILDLIFE_DETECTION,
    label: "Wildlife Detection",
    iconName: "TreePine",
    description: "Detect wildlife presence in images and videos",
    category: "standard",
    status: "active",
    isConfigBased: false,
    color: "bg-green-500",
    acceptedFileTypes: [
      "video/mp4",
      "video/avi",
      "video/mov",
      "video/quicktime",
      "video/x-msvideo",
      "image/jpeg",
      "image/jpg",
      "image/png",
    ],
    acceptedExtensions: [".mp4", ".avi", ".mov", ".jpg", ".jpeg", ".png"],
    maxFileSize: 500,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // ADVANCED ANALYSIS TYPES
  // ─────────────────────────────────────────────────────────────────────────

  [JobType.DAMAGE_DETECTION_3D]: {
    type: JobType.DAMAGE_DETECTION_3D,
    label: "3D Damage Detection",
    iconName: "Package",
    description: "Identify and analyze damage in 3D surface meshes",
    category: "advanced",
    status: "active",
    isConfigBased: false,
    externalService: "damage_detection_api",
    color: "bg-red-500",
    acceptedFileTypes: [
      "model/stl",
      "application/sla",
      "application/vnd.ms-pki.stl",
      "model/obj",
      "application/ply",
      "model/step",
      "application/step",
      "application/octet-stream",
    ],
    acceptedExtensions: [".stl", ".obj", ".ply", ".step", ".stp"],
    maxFileSize: 100,
  },

  [JobType.PPE_DETECTION]: {
    type: JobType.PPE_DETECTION,
    label: "PPE Kit Analysis",
    iconName: "ShieldAlert",
    description:
      "Detect Personal Protective Equipment (PPE) compliance in images and videos",
    category: "advanced",
    status: "active",
    isConfigBased: false,
    externalService: "ppe_detection_api",
    color: "bg-amber-500",
    acceptedFileTypes: [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "video/mp4",
      "video/quicktime",
      "video/x-matroska",
      "video/x-msvideo",
    ],
    acceptedExtensions: [
      ".jpg",
      ".jpeg",
      ".png",
      ".webp",
      ".mp4",
      ".mov",
      ".mkv",
      ".avi",
    ],
    maxFileSize: 500,
  },

  [JobType.LIDAR_DATA]: {
    type: JobType.LIDAR_DATA,
    label: "LIDAR Data Analysis",
    iconName: "Radio",
    description: "Capture and analyze LIDAR sensor data",
    category: "advanced",
    status: "active",
    isConfigBased: false,
    externalLink: "https://mesh-detecto.lovable.app/",
    color: "bg-emerald-500",
    acceptedFileTypes: [],
    acceptedExtensions: [],
    maxFileSize: 0,
  },

  [JobType.RADAR_DATA]: {
    type: JobType.RADAR_DATA,
    label: "RADAR Data Analysis",
    iconName: "Radio",
    description: "Process radar-based input for advanced analytics",
    category: "advanced",
    status: "coming_soon",
    isConfigBased: false,
    color: "bg-blue-500",
    acceptedFileTypes: [],
    acceptedExtensions: [],
    maxFileSize: 0,
  },

  [JobType.RGB_CAMERA]: {
    type: JobType.RGB_CAMERA,
    label: "RGB Camera",
    iconName: "Camera",
    description: "Integrate standard RGB camera inputs",
    category: "advanced",
    status: "coming_soon",
    isConfigBased: false,
    color: "bg-yellow-500",
    acceptedFileTypes: [],
    acceptedExtensions: [],
    maxFileSize: 0,
  },

  [JobType.THERMAL_CAMERA]: {
    type: JobType.THERMAL_CAMERA,
    label: "Thermal Camera",
    iconName: "ThermometerSun",
    description: "Upload and analyze thermal imaging data",
    category: "advanced",
    status: "coming_soon",
    isConfigBased: false,
    color: "bg-red-500",
    acceptedFileTypes: [],
    acceptedExtensions: [],
    maxFileSize: 0,
  },

  [JobType.MEDICAL_IMAGING]: {
    type: JobType.MEDICAL_IMAGING,
    label: "Medical Imaging",
    iconName: "Stethoscope",
    description: "Advanced analysis for medical imaging data",
    category: "advanced",
    status: "coming_soon",
    isConfigBased: false,
    color: "bg-purple-500",
    acceptedFileTypes: [],
    acceptedExtensions: [],
    maxFileSize: 0,
  },

  [JobType.BORESCOPIC_CAMERA]: {
    type: JobType.BORESCOPIC_CAMERA,
    label: "Borescopic Camera",
    iconName: "Microscope",
    description: "Inspect confined spaces and internal machinery components",
    category: "advanced",
    status: "coming_soon",
    isConfigBased: false,
    color: "bg-emerald-500",
    acceptedFileTypes: [],
    acceptedExtensions: [],
    maxFileSize: 0,
  },

  [JobType.PRODUCT_DESIGN_ENGINEERING]: {
    type: JobType.PRODUCT_DESIGN_ENGINEERING,
    label: "Product Design & Engineering",
    iconName: "Cog",
    description: "Engineering drawing analysis and product re-engineering",
    category: "advanced",
    status: "coming_soon",
    isConfigBased: false,
    color: "bg-indigo-500",
    acceptedFileTypes: [],
    acceptedExtensions: [],
    maxFileSize: 0,
  },

  [JobType.SATELLITE_IMAGERY]: {
    type: JobType.SATELLITE_IMAGERY,
    label: "Satellite Imagery",
    iconName: "MapPin",
    description: "Analyze satellite imagery for geospatial insights",
    category: "advanced",
    status: "coming_soon",
    isConfigBased: false,
    color: "bg-sky-500",
    acceptedFileTypes: [],
    acceptedExtensions: [],
    maxFileSize: 0,
  },

  [JobType.MULTI_CAMERA_SYSTEM]: {
    type: JobType.MULTI_CAMERA_SYSTEM,
    label: "Multi-Camera System",
    iconName: "Camera",
    description: "Configure and monitor up to 10 camera feeds in real-time",
    category: "advanced",
    status: "active",
    isConfigBased: true,
    color: "bg-violet-500",
    acceptedFileTypes: [],
    acceptedExtensions: [],
    maxFileSize: 0,
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const getStandardJobTypes = (): JobTypeConfig[] => {
  return Object.values(JOB_TYPE_CONFIGS).filter(
    (config) => config.category === "standard" && config.status === "active"
  );
};

export const getAdvancedJobTypes = (): JobTypeConfig[] => {
  return Object.values(JOB_TYPE_CONFIGS).filter(
    (config) => config.category === "advanced"
  );
};

export const getActiveJobTypes = (): JobTypeConfig[] => {
  return Object.values(JOB_TYPE_CONFIGS).filter(
    (config) => config.status === "active" && !config.externalLink
  );
};

export const getJobTypeConfig = (type: JobType): JobTypeConfig => {
  return JOB_TYPE_CONFIGS[type];
};

export const isConfigBasedJobType = (type: JobType): boolean => {
  return JOB_TYPE_CONFIGS[type]?.isConfigBased ?? false;
};

export const hasExternalLink = (type: JobType): boolean => {
  return !!JOB_TYPE_CONFIGS[type]?.externalLink;
};

export const getExternalLink = (type: JobType): string | undefined => {
  return JOB_TYPE_CONFIGS[type]?.externalLink;
};

export const isValidFileExtension = (file: File, jobType: JobType): boolean => {
  const config = JOB_TYPE_CONFIGS[jobType];
  if (!config) return false;

  const fileName = file.name.toLowerCase();
  return config.acceptedExtensions.some((ext) =>
    fileName.endsWith(ext.toLowerCase())
  );
};

export const getAcceptedExtensionsString = (jobType: JobType): string => {
  const config = JOB_TYPE_CONFIGS[jobType];
  if (!config) return "";
  return config.acceptedExtensions.join(", ");
};

export const GENERAL_JOB_TYPES = getStandardJobTypes();
export const ADVANCED_JOB_TYPE_CONFIGS = getAdvancedJobTypes();

export const JOB_STATUS_COLORS = {
  pending: "text-yellow-400 bg-yellow-500/20",
  processing: "text-blue-400 bg-blue-500/20",
  completed: "text-green-400 bg-green-500/20",
  failed: "text-red-400 bg-red-500/20",
} as const;

export const JOB_STATUS_EMOJIS = {
  pending: "⏳",
  processing: "⚙️",
  completed: "✅",
  failed: "❌",
} as const;
